import { useEffect, useRef, useState } from 'react';
import { cleanLook, type Look } from '../game/avatar';
import {
  BOSS, drawBoss, computeLiquid, drawPowerAura, POWERUP_RESPAWN, POWERUPS, drawCannon, drawRobot, drawShot, findPlatforms, drawPlatform, PLATFORM_SPEED, LAVA, WATER, drawBackground, drawBee, drawCheckpoint, drawCrab, drawGlobe, drawLiquid, drawTile,
  GLOBE_H, GLOBE_W, HAZARD, SOLID, type Level,
} from '../game/levels';
import { sfxBoing, sfxCoin, sfxHurt, sfxJump, sfxRare, sfxShoot } from '../game/sfx';
import { useTouch } from './TouchControls';

// ─────────────────────────────────────────────────────────────────────────────
// Plays a student-made level (game/levels.ts): a 2D platformer on dry land.
// You're your own fish, riding a wheeled fishbowl. Arrows or A/D roll, Space,
// W or Up jumps (hold for higher). Spikes, cacti, bees and crabs (unless you
// land on them) send you back to the last checkpoint; reach the flag to win.
// Physics runs at a fixed 120 steps a second, in tiles.
// ─────────────────────────────────────────────────────────────────────────────

const STEP = 1 / 120;
const PHYS = {
  normal: { grav: 55, jump: 19, fall: 22 },
  floaty: { grav: 30, jump: 15, fall: 12 },
};
const RUN = 6.5;

export type LevelResult = { won: boolean; secs: number; deaths: number; coins: number; coinsTotal: number; why?: 'time' | 'quit' };

type Crab = { x: number; y: number; vx: number; vy: number; alive: boolean; squish: number; bot: boolean; cool: number; charge: number };
type Cannon = { x: number; y: number; angle: number; cool: number; kick: number };
type Shot = { x: number; y: number; vx: number; vy: number; kind: 'ball' | 'laser' | 'rock' | 'wave'; life: number };
type Boss = { x: number; y: number; vx: number; vy: number; hp: number; hurt: number; cool: number; ground: boolean; alive: boolean; dead: number; dir: number; attacks: number };
type Bee = { x: number; y0: number; phase: number; gone?: boolean };

type Props = {
  level: Level;
  look: unknown;
  title: string;
  /** Called once when the run ends (win, out of time, or giving up). */
  onDone?: (r: LevelResult) => void;
  onClose: () => void;
  /** Shown under the result. */
  footer?: React.ReactNode;
};

export default function LevelPlayer(p: Props) {
  const touch = useTouch();
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const keys = useRef({ left: false, right: false, jump: false });
  const [hud, setHud] = useState({ coins: 0, keys: 0, deaths: 0, secs: 0, shield: false, star: 0, wings: 0, speed: 0, boss: -1 });
  const [msg, setMsg] = useState('');
  const [result, setResult] = useState<LevelResult | null>(null);
  const [round, setRound] = useState(0);
  const doneRef = useRef(p.onDone);
  doneRef.current = p.onDone;
  const quitRef = useRef<() => void>(() => {});

  useEffect(() => {
    const L = p.level, phys = PHYS[L.gravity];
    const look: Look = p.look ? cleanLook(p.look) : cleanLook({});
    const tiles = L.tiles.split('');
    const lava = computeLiquid(L);
    // How high lava reaches in a tile (its top edge), or null if none.
    const liquidTop = (x: number, y: number, k: number) => { if (x < 0 || x >= L.w || y < 0 || y >= L.h) return null; const i = y * L.w + x, d = lava.depth[i]; return d && lava.kind[i] === k ? y + 1 - d : null; };
    const lavaTop = (x: number, y: number) => liquidTop(x, y, LAVA);
    /** Is the point (x, y), in tiles, under water? */
    const wet = (x: number, y: number) => { const top = liquidTop(Math.floor(x), Math.floor(y), WATER); return top != null && y > top; };
    const at = (x: number, y: number) => (x < 0 || x >= L.w || y < 0 || y >= L.h ? '.' : tiles[y * L.w + x]);

    // Creatures leave the grid and move on their own.
    const crabs: Crab[] = [], bees: Bee[] = [], cannons: Cannon[] = [], shots: Shot[] = [], bosses: Boss[] = [];
    const plats = findPlatforms(L);
    for (let i = 0; i < tiles.length; i++) if (tiles[i] === 'm' || tiles[i] === 'v') tiles[i] = '.';
    let start = { x: 1, y: L.h - 3 }, coinsTotal = 0;
    for (let y = 0; y < L.h; y++) for (let x = 0; x < L.w; x++) {
      const ch = tiles[y * L.w + x];
      if (ch === 'r' || ch === 'q') { crabs.push({ x: x + 0.1, y: y + 0.4, vx: ch === 'q' ? -1.5 : -2, vy: 0, alive: true, squish: 0, bot: ch === 'q', cool: 1 + Math.random(), charge: 0 }); tiles[y * L.w + x] = '.'; }
      else if (ch === 'B') { bosses.push({ x: x + 0.5 - BOSS.w / 2, y: y + 1 - BOSS.h, vx: 0, vy: 0, hp: BOSS.hp, hurt: 0, cool: 2.5, ground: false, alive: true, dead: 0, dir: -1, attacks: 0 }); tiles[y * L.w + x] = '.'; }
      else if (ch === 'n') cannons.push({ x, y, angle: Math.PI, cool: 1 + Math.random(), kick: 0 });   // stays in the grid: it's a block
      else if (ch === 'f') { bees.push({ x: x + 0.5, y0: y + 0.5, phase: x * 0.7 }); tiles[y * L.w + x] = '.'; }
      else if (ch === 'S') { start = { x, y }; tiles[y * L.w + x] = '.'; }
      else if (ch === 'o') coinsTotal++;
    }
    const spawn = (c: { x: number; y: number }) => ({ x: c.x + (1 - GLOBE_W) / 2, y: c.y + 1 - GLOBE_H });
    let check = start;
    const pl = { ...spawn(start), vx: 0, vy: 0, ground: false, facing: 1 as 1 | -1, roll: 0, coyote: 0, buffer: 0, squash: 0, safe: 0, hurt: 0, jumpHeld: false, wasWet: false, riding: -1,
      // Power-ups: a shield until it's hit; the others are seconds left.
      shield: false, star: 0, wings: 0, speed: 0, airJump: false };
    const comeback: { i: number; ch: string; at: number }[] = [];   // taken power-ups, and when they return
    let coins = 0, keyCount = 0, deaths = 0, secs = 0, over = false, ready = 1.0, t = 0;
    let msgTimer = 0, lastMsg = '';

    const solid = (x: number, y: number) => {
      if (x < 0 || x >= L.w) return true;                        // the level's sides are walls
      const ch = at(x, y);
      return SOLID.has(ch) || ch === 'd' || lava.rock[y * L.w + x] === 1;   // stone where water met lava
    };
    const say = (m: string) => { if (m === lastMsg && msgTimer > 1) return; lastMsg = m; setMsg(m); msgTimer = 2; };

    const finish = (won: boolean, why?: LevelResult['why']) => {
      if (over) return;
      over = true;
      const r = { won, secs: Math.round(secs * 10) / 10, deaths, coins, coinsTotal, why };
      if (won) sfxRare();
      setResult(r);
      doneRef.current?.(r);
    };
    quitRef.current = () => finish(false, 'quit');

    /** A hit or a fall. A star shrugs off hits; a shield takes one (and gives a moment to get clear). Falling out of the level always counts. */
    const die = (fall = false) => {
      if (pl.safe > 0 || over) return;
      if (!fall && pl.star > 0) return;
      if (!fall && pl.shield) { pl.shield = false; pl.safe = 1; pl.hurt = 0.2; sfxBoing(); say('🛡️ Your shield saved you!'); return; }
      deaths++; sfxHurt();
      pl.hurt = 0.4;
      Object.assign(pl, spawn(check), { vx: 0, vy: 0, safe: 0.8, shield: false, star: 0, wings: 0, speed: 0 });
      shots.length = 0;                                          // a fresh start: nothing already flying at you
    };

    // Opens a door you bump into, if you have a key.
    const tryDoor = (x: number, y: number) => {
      if (at(x, y) === 'd' && keyCount > 0) { keyCount--; tiles[y * L.w + x] = 'D'; sfxBoing(); return true; }
      if (at(x, y) === 'd') say('🔑 This door needs a key');
      return false;
    };

    const step = (dt: number) => {
      t += dt;
      if (ready > 0) { ready -= dt; return; }
      if (over) return;
      secs += dt;
      if (L.time > 0 && secs >= L.time) { finish(false, 'time'); return; }
      pl.safe = Math.max(0, pl.safe - dt); pl.hurt = Math.max(0, pl.hurt - dt); pl.squash = Math.max(0, pl.squash - dt * 4);
      if (msgTimer > 0) { msgTimer -= dt; if (msgTimer <= 0) { setMsg(''); lastMsg = ''; } }

      // Moving platforms go back and forth, carrying whoever is standing on them.
      const boxHits = (x: number, y: number) => {
        for (let ty = Math.floor(y); ty <= Math.floor(y + GLOBE_H - 0.001); ty++)
          for (let tx = Math.floor(x); tx <= Math.floor(x + GLOBE_W - 0.001); tx++) if (solid(tx, ty)) return true;
        return false;
      };
      plats.forEach((p, i) => {
        const was = p.axis === 'x' ? p.x : p.y;
        let now = was + p.dir * PLATFORM_SPEED * dt;
        if (now <= p.min) { now = p.min; p.dir = 1; }
        if (now >= p.max) { now = p.max; p.dir = -1; }
        if (p.axis === 'x') p.x = now; else p.y = now;
        if (pl.riding !== i) return;
        const d = now - was;
        if (p.axis === 'x') { if (!boxHits(pl.x + d, pl.y)) pl.x += d; }
        else if (!boxHits(pl.x, pl.y + d)) pl.y += d;
      });

      // Rolling: ice is slippery.
      const k = keys.current;
      const under = at(Math.floor(pl.x + GLOBE_W / 2), Math.floor(pl.y + GLOBE_H + 0.05));
      const icy = pl.ground && under === 'i';
      const want = (k.right ? 1 : 0) - (k.left ? 1 : 0);
      if (want) pl.facing = want > 0 ? 1 : -1;
      // In water (the bowl's middle under the surface): slower, sinking gently; hold jump to swim up.
      const cx = pl.x + GLOBE_W / 2;
      const swimming = wet(cx, pl.y + GLOBE_H * 0.55);
      if (swimming && !pl.wasWet) { pl.vy *= 0.3; sfxBoing(); }
      pl.wasWet = swimming;
      if (pl.star > 0) pl.star = Math.max(0, pl.star - dt);
      if (pl.wings > 0) pl.wings = Math.max(0, pl.wings - dt);
      if (pl.speed > 0) pl.speed = Math.max(0, pl.speed - dt);
      const top = RUN * (swimming ? 0.6 : 1) * (pl.speed > 0 ? 1.6 : 1);
      const accel = swimming ? 20 : pl.ground ? (icy ? 10 : 55) : 32;
      const decel = swimming ? 10 : pl.ground ? (icy ? 2.5 : 45) : 8;
      if (want) pl.vx += Math.sign(want * top - pl.vx) * Math.min(Math.abs(want * top - pl.vx), accel * dt);
      else pl.vx -= Math.sign(pl.vx) * Math.min(Math.abs(pl.vx), decel * dt);

      // Jumping, with a moment of grace after leaving a ledge and before landing.
      pl.coyote = pl.ground ? 0.1 : pl.coyote - dt;
      if (k.jump && !pl.jumpHeld) pl.buffer = 0.12; else pl.buffer -= dt;
      pl.jumpHeld = k.jump;
      if (swimming) {
        // At the surface, a jump hops out of the water; under it, holding jump swims up.
        const atSurface = !wet(cx, pl.y + GLOBE_H * 0.15);
        if (atSurface && pl.buffer > 0) { pl.vy = -phys.jump; pl.buffer = 0; sfxJump(); }
        else if (k.jump) pl.vy = Math.max(-4.5, pl.vy - 30 * dt);
        pl.vy = Math.min(2.5, pl.vy + phys.grav * 0.25 * dt);
      } else {
        if (pl.ground) pl.airJump = true;
        if (pl.buffer > 0 && pl.coyote > 0) { pl.vy = -phys.jump; pl.coyote = 0; pl.buffer = 0; pl.ground = false; sfxJump(); }
        else if (pl.buffer > 0 && pl.wings > 0 && pl.airJump) { pl.vy = -phys.jump * 0.9; pl.airJump = false; pl.buffer = 0; sfxJump(); }   // wings: a second jump
        if (!k.jump && pl.vy < -phys.jump * 0.45) pl.vy = -phys.jump * 0.45;   // let go early: a shorter hop
        pl.vy = Math.min(phys.fall, pl.vy + phys.grav * dt);
      }

      // Move across, then up or down, stopping at walls and floors.
      pl.x += pl.vx * dt;
      for (let y = Math.floor(pl.y); y <= Math.floor(pl.y + GLOBE_H - 0.001); y++) {
        if (pl.vx > 0) { const x = Math.floor(pl.x + GLOBE_W); if (solid(x, y) && !tryDoor(x, y)) { pl.x = x - GLOBE_W; pl.vx = 0; } }
        else if (pl.vx < 0) { const x = Math.floor(pl.x); if (solid(x, y) && !tryDoor(x, y)) { pl.x = x + 1; pl.vx = 0; } }
      }
      const prevBottom = pl.y + GLOBE_H;
      pl.y += pl.vy * dt;
      const wasGround = pl.ground;
      pl.ground = false;
      for (let x = Math.floor(pl.x); x <= Math.floor(pl.x + GLOBE_W - 0.001); x++) {
        if (pl.vy >= 0) {
          const y = Math.floor(pl.y + GLOBE_H);
          const ch = at(x, y);
          if ((solid(x, y) && !tryDoor(x, y)) || (ch === 'p' && prevBottom <= y + 0.02)) {
            if (!wasGround && pl.vy > 8) pl.squash = Math.min(1, pl.vy / 30);
            pl.y = y - GLOBE_H; pl.vy = 0; pl.ground = true;
          }
        } else {
          const y = Math.floor(pl.y);
          if (solid(x, y) && !tryDoor(x, y)) { pl.y = y + 1; pl.vy = 0; }
        }
      }
      // Landing on (or still standing on) a moving platform: like a ledge, you can jump up through it.
      pl.riding = -1;
      if (pl.vy >= 0) plats.forEach((p, i) => {
        if (pl.x + GLOBE_W <= p.x + 0.05 || pl.x >= p.x + p.w - 0.05) return;
        if (prevBottom <= p.y + 0.06 && pl.y + GLOBE_H >= p.y) {
          if (!wasGround && pl.vy > 8) pl.squash = Math.min(1, pl.vy / 30);
          pl.y = p.y - GLOBE_H; pl.vy = 0; pl.ground = true; pl.riding = i;
        }
      });
      pl.roll += (pl.vx * dt) / 0.13;

      // What the bowl is touching.
      const x0 = pl.x, y0 = pl.y, x1 = pl.x + GLOBE_W, y1 = pl.y + GLOBE_H;
      for (let y = Math.floor(y0); y <= Math.floor(y1 - 0.001); y++) for (let x = Math.floor(x0); x <= Math.floor(x1 - 0.001); x++) {
        const ch = at(x, y);
        const lt = lavaTop(x, y);
        if (lt != null && y1 > lt + 0.12 && x1 > x + 0.1 && x0 < x + 0.9) die();
        if (HAZARD.has(ch)) {
          // The pointy part only: the top of a spike row sits low in its tile.
          if (x1 > x + 0.2 && x0 < x + 0.8 && y1 > y + (ch === 'x' ? 0.45 : 0.2)) die();
        } else if (ch === 'o') { tiles[y * L.w + x] = '.'; coins++; sfxCoin(); }
        else if (POWERUPS[ch]) {
          const p = POWERUPS[ch];
          tiles[y * L.w + x] = '.'; comeback.push({ i: y * L.w + x, ch, at: secs + POWERUP_RESPAWN });
          if (ch === 'y') pl.shield = true; else if (ch === 't') pl.star = p.secs; else if (ch === 'e') pl.wings = p.secs; else pl.speed = p.secs;
          sfxRare(); say(`${p.icon} ${p.name}!${p.secs ? ` (${p.secs} s)` : ''}`);
        }
        else if (ch === 'k') { tiles[y * L.w + x] = '.'; keyCount++; sfxCoin(); say('🔑 Got a key! Roll into a door to open it'); }
        else if (ch === 'h' && (check.x !== x || check.y !== y)) { check = { x, y }; sfxBoing(); say('✅ Checkpoint!'); }
        else if (ch === 'j' && pl.vy >= 0 && y1 > y + 0.4) { pl.vy = -phys.jump * 1.45; pl.squash = 0.6; sfxBoing(); }
        else if (ch === 'G') {
          if (L.allCoins && coins < coinsTotal) say(`🪙 Collect every coin first (${coins}/${coinsTotal})`);
          else if (bosses.some(b => b.alive)) say(`👑 Beat the ${BOSS.name} first!`);
          else finish(true);
        }
      }
      if (pl.y > L.h + 1) die(true);

      // Crabs walk, turn at walls and ledges, and can be squished from above.
      for (const c of crabs) {
        if (!c.alive) { c.squish += dt; continue; }
        c.vy = Math.min(phys.fall, c.vy + phys.grav * dt);
        // Robots stop, light up, and fire when you're ahead of them on about the same level.
        if (c.bot) {
          c.cool -= dt;
          const dir = Math.sign(c.vx), dx = (pl.x + GLOBE_W / 2) - (c.x + 0.4), dy = (pl.y + GLOBE_H / 2) - (c.y + 0.3);
          if (c.cool <= 0 && Math.sign(dx) === dir && Math.abs(dx) < 9 && Math.abs(dy) < 1.2 && pl.safe <= 0) {
            c.charge += dt;
            if (c.charge >= 0.5) {
              shots.push({ x: c.x + 0.4 + dir * 0.45, y: c.y + 0.3, vx: dir * 8, vy: 0, kind: 'laser', life: 3 });
              sfxShoot('laser'); c.charge = 0; c.cool = 2;
            }
          } else c.charge = 0;
        }
        if (!c.charge) c.x += c.vx * dt;
        const ahead = c.vx > 0 ? Math.floor(c.x + 0.8) : Math.floor(c.x);
        const feet = Math.floor(c.y + 0.6 + 0.05);
        if (solid(ahead, Math.floor(c.y + 0.3)) || !(solid(ahead, feet) || at(ahead, feet) === 'p')) { c.vx = -c.vx; c.x += c.vx * dt * 2; }
        c.y += c.vy * dt;
        const below = Math.floor(c.y + 0.6);
        for (const bx of [Math.floor(c.x + 0.1), Math.floor(c.x + 0.7)]) if (solid(bx, below) || at(bx, below) === 'p') { c.y = below - 0.6; c.vy = 0; }
        if (c.y > L.h + 2) c.alive = false;
        const cl = lavaTop(Math.floor(c.x + 0.4), Math.floor(c.y + 0.5));
        if (cl != null && c.y + 0.6 > cl + 0.1) c.alive = false;   // crabs don't like lava either
        if (x1 > c.x + 0.05 && x0 < c.x + 0.75 && y1 > c.y + 0.1 && y0 < c.y + 0.6) {
          if (pl.vy > 0 && y1 - c.y < 0.45) { c.alive = false; pl.vy = -phys.jump * 0.6; sfxBoing(); }
          else if (pl.star > 0) { c.alive = false; sfxBoing(); }            // a star knocks creatures out
          else die();
        }
      }
      // The King Crab: walks at you, then leaps (shockwaves where it lands) or throws rocks. Stomp its head.
      for (const b of bosses) {
        if (!b.alive) { b.dead += dt; continue; }
        const bcx = b.x + BOSS.w / 2, angry = b.hp <= 2;
        const awake = Math.abs(pl.x - bcx) < 16 && Math.abs(pl.y - b.y) < 10;
        b.hurt = Math.max(0, b.hurt - dt);
        const wasAir = !b.ground;
        if (b.ground && awake) {
          b.dir = Math.sign(pl.x + GLOBE_W / 2 - bcx) || b.dir;
          // Stops at walls and at edges (it won't walk off a ledge).
          const ahead = b.dir > 0 ? Math.floor(b.x + BOSS.w + 0.05) : Math.floor(b.x - 0.05);
          const feet = Math.floor(b.y + BOSS.h + 0.05);
          const blocked = solid(ahead, Math.floor(b.y + BOSS.h - 0.5)) || !(solid(ahead, feet) || at(ahead, feet) === 'p');
          b.vx = blocked ? 0 : b.dir * (angry ? 2.6 : 1.6);
          b.cool -= dt;
          if (b.cool <= 0) {
            b.attacks++;
            if (b.attacks % 2) { b.vy = -16; b.vx = b.dir * 3.5; b.ground = false; sfxJump(); }                 // leap
            else {                                                                                       // a spread of rocks
              const n = angry ? 5 : 3;
              for (let k = 0; k < n; k++) shots.push({ x: bcx, y: b.y + 0.2, vx: b.dir * (2.5 + k * 1.6), vy: -9 - (k % 2) * 2, kind: 'rock', life: 4 });
              sfxShoot('ball');
            }
            b.cool = angry ? 1.8 : 2.8;
          }
        } else if (b.ground) b.vx = 0;
        b.vy = Math.min(phys.fall, b.vy + phys.grav * dt);
        // Move, stopping at walls and floors.
        b.x += b.vx * dt;
        for (let ty = Math.floor(b.y); ty <= Math.floor(b.y + BOSS.h - 0.05); ty++) {
          if (b.vx > 0 && solid(Math.floor(b.x + BOSS.w), ty)) { b.x = Math.floor(b.x + BOSS.w) - BOSS.w; b.vx = 0; }
          if (b.vx < 0 && solid(Math.floor(b.x), ty)) { b.x = Math.floor(b.x) + 1; b.vx = 0; }
        }
        b.y += b.vy * dt; b.ground = false;
        for (let tx = Math.floor(b.x); tx <= Math.floor(b.x + BOSS.w - 0.01); tx++) {
          const ty = Math.floor(b.y + BOSS.h);
          if (b.vy >= 0 && (solid(tx, ty) || at(tx, ty) === 'p')) { b.y = ty - BOSS.h; b.vy = 0; b.ground = true; }
        }
        if (b.ground && wasAir && awake) {                                                              // landing: shockwaves both ways
          for (const d of [-1, 1]) shots.push({ x: bcx + d * BOSS.w * 0.5, y: b.y + BOSS.h - 0.3, vx: d * 6, vy: 0, kind: 'wave', life: 2.5 });
          sfxShoot('ball');
        }
        if (b.y > L.h + 2) { b.alive = false; say(`👑 The ${BOSS.name} fell! You win the fight!`); }
        // Touching it: a stomp on the head hurts it; anything else hurts you (a star hurts it too).
        if (x1 > b.x + 0.15 && x0 < b.x + BOSS.w - 0.15 && y1 > b.y + 0.2 && y0 < b.y + BOSS.h) {
          const stomp = pl.vy > 0 && y1 - b.y < 0.6;
          if ((stomp || pl.star > 0) && b.hurt <= 0) {
            b.hp--; b.hurt = 1.2; sfxBoing();
            if (stomp) pl.vy = -phys.jump * 0.9;
            if (b.hp <= 0) { b.alive = false; sfxRare(); say(`👑 You beat the ${BOSS.name}!`); }
          } else if (stomp) pl.vy = -phys.jump * 0.6;
          else if (b.hurt <= 0) die();
        }
      }

      // Cannons turn to aim at you and fire when you're near.
      const pcx = pl.x + GLOBE_W / 2, pcy = pl.y + GLOBE_H / 2;
      for (const cn of cannons) {
        const ox = cn.x + 0.5, oy = cn.y + 0.42, dx = pcx - ox, dy = pcy - oy;
        const want = Math.atan2(dy, dx);
        let turn = ((want - cn.angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        turn = Math.max(-3 * dt, Math.min(3 * dt, turn));
        cn.angle += turn;
        cn.kick = Math.max(0, cn.kick - dt * 4);
        cn.cool -= dt;
        if (cn.cool <= 0 && dx * dx + dy * dy < 11 * 11 && pl.safe <= 0) {
          shots.push({ x: ox + Math.cos(cn.angle) * 0.6, y: oy + Math.sin(cn.angle) * 0.6, vx: Math.cos(cn.angle) * 6, vy: Math.sin(cn.angle) * 6, kind: 'ball', life: 4 });
          sfxShoot('ball'); cn.cool = 2.4; cn.kick = 1;
        }
      }
      // Projectiles fly straight; blocks stop them; touching one is a fall.
      for (let k = shots.length - 1; k >= 0; k--) {
        const sh = shots[k];
        if (sh.kind === 'rock') sh.vy += 20 * dt;                // rocks arc down
        sh.x += sh.vx * dt; sh.y += sh.vy * dt; sh.life -= dt;
        // A shockwave rolls along the ground and stops at a wall or an edge.
        const offEdge = sh.kind === 'wave' && !solid(Math.floor(sh.x), Math.floor(sh.y + 0.5));
        if (offEdge || sh.life <= 0 || sh.y > L.h + 1 || solid(Math.floor(sh.x), Math.floor(sh.y))) { shots.splice(k, 1); continue; }
        const r = sh.kind === 'ball' || sh.kind === 'rock' ? 0.18 : sh.kind === 'wave' ? 0.28 : 0.1;
        const nx = Math.max(x0, Math.min(sh.x, x1)), ny = Math.max(y0, Math.min(sh.y, y1));
        if ((nx - sh.x) ** 2 + (ny - sh.y) ** 2 < r * r) { shots.splice(k, 1); die(); break; }   // die() may clear every shot: stop here
      }
      for (const b of bees) {
        if (b.gone) continue;
        const by = b.y0 + Math.sin(t * 2 + b.phase) * 1.2;
        const nx = Math.max(x0, Math.min(b.x, x1)), ny = Math.max(y0, Math.min(by, y1));
        if ((nx - b.x) ** 2 + (ny - by) ** 2 < 0.28 ** 2) { if (pl.star > 0) { b.gone = true; sfxBoing(); } else die(); }
      }
      // Power-ups come back a while after they're taken.
      for (let k = comeback.length - 1; k >= 0; k--) if (secs >= comeback[k].at && tiles[comeback[k].i] === '.') { tiles[comeback[k].i] = comeback[k].ch; comeback.splice(k, 1); }
    };

    // ── Drawing ───────────────────────────────────────────────────────────────
    const c = canvasRef.current!, g = c.getContext('2d')!;
    let camX = 0, camY = 0, W = 0, H = 0, s = 32;
    const ROWS = Math.min(L.h, 10);                      // rows on screen at once; the camera follows up and down
    const fit = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      W = wrapRef.current?.clientWidth ?? 600;
      // Wide and short on a computer; taller on a phone held upright.
      H = Math.max(220, Math.min(W * (W < 600 ? 0.95 : 0.56), window.innerHeight * (W < 600 ? 0.5 : 0.7), 560));
      s = H / ROWS;
      c.width = Math.round(W * dpr); c.height = Math.round(H * dpr);
      c.style.width = `${W}px`; c.style.height = `${H}px`;
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    fit();
    camX = (pl.x + GLOBE_W / 2) * s - W / 2; camY = L.h * s - H;
    const ro = new ResizeObserver(fit);
    if (wrapRef.current) ro.observe(wrapRef.current);

    const draw = () => {
      const target = (pl.x + GLOBE_W / 2) * s - W / 2;
      camX += (target - camX) * 0.15;
      camX = Math.max(0, Math.min(L.w * s - W, camX));
      if (L.w * s <= W) camX = (L.w * s - W) / 2;
      const targetY = (pl.y + GLOBE_H / 2) * s - H * 0.55;
      camY += (targetY - camY) * 0.12;
      camY = Math.max(0, Math.min(L.h * s - H, camY));
      drawBackground(g, L.theme, W, H, camX, t);
      g.save(); g.translate(-Math.round(camX), -Math.round(camY));
      const fx = Math.max(0, Math.floor(camX / s)), lx = Math.min(L.w - 1, Math.ceil((camX + W) / s));
      const fy = Math.max(0, Math.floor(camY / s)), ly = Math.min(L.h - 1, Math.ceil((camY + H) / s));
      for (let y = fy; y <= ly; y++) for (let x = fx; x <= lx; x++) {
        const ch = tiles[y * L.w + x];
        if (ch === '.') continue;
        if (ch === 'h') drawCheckpoint(g, x * s, y * s, s, check.x === x && check.y === y);
        else if (ch === 'D') drawTile(g, 'd', x * s, y * s, s, t, true);
        else if (ch !== 'l' && ch !== 'a' && ch !== 'n') drawTile(g, ch, x * s, y * s, s, t);
      }
      drawLiquid(g, L, lava, s, t, fx, lx, fy, ly, 'lava');
      for (const p of plats) drawPlatform(g, p.x * s, p.y * s, p.w * s, s, p.axis, false);
      for (const cr of crabs) {
        const art = cr.bot ? drawRobot : drawCrab, oy = cr.bot ? 0.2 : 0.32;
        if (cr.alive) cr.bot ? drawRobot(g, (cr.x + 0.4) * s, (cr.y + oy) * s, s, cr.charge ? 0 : t, Math.sign(cr.vx), cr.charge / 0.5) : drawCrab(g, (cr.x + 0.4) * s, (cr.y + oy) * s, s, t, Math.sign(cr.vx));
        else if (cr.squish < 0.4) { g.save(); g.translate((cr.x + 0.4) * s, (cr.y + 0.55) * s); g.scale(1.2, 0.35); art(g, 0, 0, s, 0, 1); g.restore(); }
      }
      for (const b of bees) if (!b.gone) drawBee(g, b.x * s, (b.y0 + Math.sin(t * 2 + b.phase) * 1.2) * s, s * 0.9, t);
      for (const b of bosses) {
        const bcx = (b.x + BOSS.w / 2) * s, bot = (b.y + BOSS.h) * s;
        if (b.alive) drawBoss(g, bcx, bot, s, t, b.dir, b.hurt > 0 && Math.floor(t * 12) % 2 === 0, b.hp <= 2);
        else if (b.dead < 0.8) { g.save(); g.globalAlpha = 1 - b.dead / 0.8; g.translate(bcx, bot); g.rotate(b.dead * 4); g.scale(1 - b.dead, 1 - b.dead); drawBoss(g, 0, 0, s, t, b.dir, true, false); g.restore(); }
      }
      for (const cn of cannons) if (cn.x >= fx - 1 && cn.x <= lx + 1) drawCannon(g, cn.x * s, cn.y * s, s, cn.angle, cn.kick);
      for (const sh of shots) drawShot(g, sh.x * s, sh.y * s, s, sh.kind, sh.vx, sh.vy);
      if (!(pl.safe > 0 && Math.floor(t * 12) % 2)) {
        drawGlobe(g, look, { cx: (pl.x + GLOBE_W / 2) * s, bottom: (pl.y + GLOBE_H) * s, s, t, facing: pl.facing, roll: pl.roll, vx: pl.vx, vy: pl.vy, squash: pl.squash, hurt: pl.hurt > 0 });
        drawPowerAura(g, (pl.x + GLOBE_W / 2) * s, (pl.y + GLOBE_H) * s, s, t, { shield: pl.shield, star: pl.star, wings: pl.wings, speed: pl.speed, facing: pl.facing });
      }
      drawLiquid(g, L, lava, s, t, fx, lx, fy, ly, 'water');   // over the player, so they look under water
      g.restore();
      if (ready > 0) {
        g.fillStyle = 'rgba(0,0,0,0.25)'; g.fillRect(0, 0, W, H);
        g.fillStyle = '#fff'; g.font = `900 ${Math.round(H * 0.16)}px system-ui`; g.textAlign = 'center'; g.textBaseline = 'middle';
        g.fillText(ready > 0.5 ? 'Ready…' : 'Go!', W / 2, H / 2);
      }
    };

    // For checking the physics in development (the test pages): step the game by hand.
    if (import.meta.env.DEV) (window as unknown as { __level?: unknown }).__level = {
      run: (secs: number, press: Partial<typeof keys.current> = {}) => { Object.assign(keys.current, press); for (let n = 0; n < secs / STEP; n++) step(STEP); return { x: pl.x, y: pl.y, vy: pl.vy, ground: pl.ground, wet: pl.wasWet, riding: pl.riding, deaths, shots: shots.length, enemies: crabs.filter(c => c.alive).map(c => +c.x.toFixed(2)), bosses: bosses.map(b => ({ hp: b.hp, x: +b.x.toFixed(2), alive: b.alive })), power: { shield: pl.shield, star: +pl.star.toFixed(1), wings: +pl.wings.toFixed(1), speed: +pl.speed.toFixed(1) }, vx: pl.vx, cannons: cannons.map(c => +c.angle.toFixed(2)), plats: plats.map(p => ({ x: p.x, y: p.y })) }; },
    };

    let raf = 0, last = performance.now(), acc = 0, hudT = 0, alive = true;
    const frame = (now: number) => {
      if (!alive) return;
      raf = requestAnimationFrame(frame);
      acc += Math.min(0.1, (now - last) / 1000); last = now;
      while (acc >= STEP) { step(STEP); acc -= STEP; }
      draw();
      hudT += 1;
      if (hudT % 6 === 0) setHud({ coins, keys: keyCount, deaths, secs: Math.floor(secs), shield: pl.shield, star: Math.ceil(pl.star), wings: Math.ceil(pl.wings), speed: Math.ceil(pl.speed), boss: (() => { const b = bosses.find(b => b.alive && Math.abs(b.x - pl.x) < 16); return b ? b.hp : -1; })() });
    };
    raf = requestAnimationFrame(frame);

    const set = (e: KeyboardEvent, down: boolean) => {
      const k = e.key.toLowerCase();
      if (k === 'arrowleft' || k === 'a') keys.current.left = down;
      else if (k === 'arrowright' || k === 'd') keys.current.right = down;
      else if (k === ' ' || k === 'arrowup' || k === 'w') keys.current.jump = down;
      else return;
      e.preventDefault();
    };
    const kd = (e: KeyboardEvent) => set(e, true), ku = (e: KeyboardEvent) => set(e, false);
    window.addEventListener('keydown', kd); window.addEventListener('keyup', ku);
    return () => {
      alive = false; cancelAnimationFrame(raf); ro.disconnect();
      window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku);
      keys.current = { left: false, right: false, jump: false };
    };
  }, [p.level, p.look, round]);

  const hold = (k: 'left' | 'right' | 'jump') => ({
    onPointerDown: (e: React.PointerEvent) => { e.preventDefault(); (e.target as HTMLElement).setPointerCapture?.(e.pointerId); keys.current[k] = true; },
    onPointerUp: () => { keys.current[k] = false; },
    onPointerCancel: () => { keys.current[k] = false; },
    onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
  });
  const coinsTotal = (p.level.tiles.match(/o/g) ?? []).length;

  return (
    <div className="level-player panel">
      <div className="panel-head">
        <h3 dir="auto">🕹️ {p.title}</h3>
        <button type="button" className="icon" aria-label="Close" onClick={p.onClose}>✕</button>
      </div>
      <div className="level-stage" ref={wrapRef}>
        <canvas ref={canvasRef} aria-label="The level. Arrow keys to roll, Space to jump." />
        <div className="level-hud">
          {coinsTotal > 0 && <span>🪙 {hud.coins}/{coinsTotal}</span>}
          {hud.keys > 0 && <span>🔑 {hud.keys}</span>}
          <span>💥 {hud.deaths}</span>
          {hud.shield && <span className="power">🛡️</span>}
          {hud.star > 0 && <span className="power">⭐ {hud.star}</span>}
          {hud.wings > 0 && <span className="power">🪽 {hud.wings}</span>}
          {hud.speed > 0 && <span className="power">⚡ {hud.speed}</span>}
          <span>⏱ {p.level.time ? Math.max(0, p.level.time - hud.secs) : hud.secs}s</span>
        </div>
        {hud.boss >= 0 && (
          <div className="boss-bar" aria-label={`${BOSS.name}: ${hud.boss} hearts left`}>
            <strong>👑 {BOSS.name}</strong>
            <span>{Array.from({ length: BOSS.hp }, (_, k) => (k < hud.boss ? '❤️' : '🖤')).join('')}</span>
          </div>
        )}
        {msg && <div className="level-msg">{msg}</div>}
        {result && (
          <div className="level-end">
            <div className="panel">
              <h2>{result.won ? '🏁 You beat it!' : result.why === 'time' ? '⏰ Out of time' : 'Game over'}</h2>
              <p>{result.secs}s · 💥 {result.deaths} fall{result.deaths === 1 ? '' : 's'}{result.coinsTotal ? ` · 🪙 ${result.coins}/${result.coinsTotal}` : ''}</p>
              {p.footer}
              <div className="row" style={{ justifyContent: 'center', gap: 8 }}>
                {!p.onDone && <button type="button" onClick={() => { setResult(null); setRound(r => r + 1); }}>Play again</button>}
                <button type="button" className="primary" onClick={p.onClose}>Done</button>
              </div>
            </div>
          </div>
        )}
      </div>
      {touch && !result && (
        <div className="level-pad">
          <div className="row" style={{ gap: 12 }}>
            <button type="button" aria-label="Roll left" {...hold('left')}>◀</button>
            <button type="button" aria-label="Roll right" {...hold('right')}>▶</button>
          </div>
          <button type="button" className="jump" aria-label="Jump" {...hold('jump')}>⤒</button>
        </div>
      )}
      <div className="row small muted level-help">
        {!touch && <span><kbd>←</kbd><kbd>→</kbd> or <kbd>A</kbd><kbd>D</kbd> roll · <kbd>Space</kbd> jump (hold to jump higher)</span>}
        <span className="spacer" />
        {!result && <button type="button" className="small" onClick={() => quitRef.current()}>Give up</button>}
      </div>
    </div>
  );
}
